import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "@fg/icons";
import { cn } from "@/lib/utils";

// The emoji list is shared with the desktop apps: Shared/emoji/emoji.json (package @fg/emoji).
import { EMOJI } from "@fg/emoji";

type EmojiCategory = string;

interface EmojiEntry {
    emoji: string;
    name: string;
    category: EmojiCategory;
    // tone variants index: 0=default,1=light,2=medium-light,3=medium,4=medium-dark,5=dark
    tones?: string[];
}

const EMOJI_DATA: EmojiEntry[] = EMOJI.emoji.map((x) => ({ emoji: x.e, name: x.n, category: x.c, tones: x.t }));
const CATEGORIES = EMOJI.categories.filter((c) => c.id !== "recent");
const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(EMOJI.categories.map((c) => [c.id, c.label]));
const CATEGORY_ORDER: EmojiCategory[] = CATEGORIES.map((c) => c.id);
const CATEGORY_ICONS: Record<string, string> = Object.fromEntries(EMOJI.categories.map((c) => [c.id, c.icon]));
const SKIN_TONE_LABELS = EMOJI.toneLabels;
const SKIN_TONE_SWATCHES = EMOJI.toneSwatches;
const COLUMNS = EMOJI.columns;

const RECENT_KEY = "fg:connect:recent-emoji";
const SKIN_TONE_KEY = "fg:connect:skin-tone";
const MAX_RECENT = EMOJI.maxRecent;

function loadRecent(): string[] {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as string[]; }
    catch { return []; }
}
function saveRecent(list: string[]) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); }
    catch { /* ignore quota errors */ }
}
function loadSkinTone(): number {
    try { return Number(localStorage.getItem(SKIN_TONE_KEY) ?? "0"); }
    catch { return 0; }
}
function saveSkinTone(tone: number) {
    try { localStorage.setItem(SKIN_TONE_KEY, String(tone)); }
    catch { /* ignore */ }
}

interface EmojiPickerProps {
    onSelect: (emoji: string) => void;
    onClose: () => void;
}

/**
 * Full emoji picker with category tabs, search, skin-tone modifier,
 * recently-used section, and full keyboard navigation.
 *
 * Sends a `connect:insert-emoji` CustomEvent on the document for the
 * MentionTextarea to intercept; also calls `onSelect` for callers that
 * own their own insertion logic.
 */
export function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
    const [query, setQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState<EmojiCategory | "recent">("smileys");
    const [skinTone, setSkinTone] = useState<number>(() => loadSkinTone());
    const [recent, setRecent] = useState<string[]>(() => loadRecent());
    const [focusIdx, setFocusIdx] = useState(0);
    const [showTonePicker, setShowTonePicker] = useState(false);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const gridRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    // Close on Escape
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.stopPropagation(); onClose(); }
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [onClose]);

    const applyTone = useCallback((entry: EmojiEntry, tone: number): string => {
        if (tone === 0 || !entry.tones) return entry.emoji;
        return entry.tones[tone - 1] ?? entry.emoji;
    }, []);

    const visibleEmojis = useMemo<string[]>(() => {
        if (query.length >= 1) {
            const q = query.toLowerCase();
            return EMOJI_DATA
                .filter((e) => e.name.includes(q) || e.emoji === q)
                .map((e) => applyTone(e, skinTone));
        }
        if (activeCategory === "recent") return recent;
        return EMOJI_DATA
            .filter((e) => e.category === activeCategory)
            .map((e) => applyTone(e, skinTone));
    }, [query, activeCategory, skinTone, recent, applyTone]);

    const pickEmoji = useCallback((emoji: string) => {
        // Update recently used
        const next = [emoji, ...recent.filter((e) => e !== emoji)].slice(0, MAX_RECENT);
        setRecent(next);
        saveRecent(next);
        onSelect(emoji);
        // Fire custom event for MentionTextarea
        document.dispatchEvent(new CustomEvent("connect:insert-emoji", { detail: { emoji } }));
        onClose();
    }, [onSelect, onClose, recent]);

    // Keyboard navigation in grid
    const onGridKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const cols = COLUMNS;
        const len = visibleEmojis.length;
        if (len === 0) return;
        if (e.key === "ArrowRight") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, len - 1)); }
        if (e.key === "ArrowLeft")  { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
        if (e.key === "ArrowDown")  { e.preventDefault(); setFocusIdx((i) => Math.min(i + cols, len - 1)); }
        if (e.key === "ArrowUp")    { e.preventDefault(); setFocusIdx((i) => Math.max(i - cols, 0)); }
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const em = visibleEmojis[focusIdx];
            if (em) pickEmoji(em);
        }
    }, [visibleEmojis, focusIdx, pickEmoji]);

    // Reset focus when list changes
    useEffect(() => { setFocusIdx(0); }, [visibleEmojis]);

    // Focus the focused cell when idx changes
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;
        const btn = grid.querySelectorAll<HTMLButtonElement>("button.emoji-cell")[focusIdx];
        btn?.focus();
    }, [focusIdx]);

    const changeSkinTone = (tone: number) => {
        setSkinTone(tone);
        saveSkinTone(tone);
        setShowTonePicker(false);
    };

    return (
        <div
            className="z-50 flex w-80 max-w-[calc(100vw-16px)] flex-col rounded-lg border bg-card shadow-xl"
            role="dialog"
            aria-label="Emoji picker"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Search bar */}
            <div className="flex items-center gap-2 border-b px-2 py-2">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                    ref={searchRef}
                    type="search"
                    placeholder="Search emoji…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    aria-label="Search emoji"
                />
                {query ? (
                    <button type="button" onClick={() => setQuery("")} className="rounded p-0.5 hover:bg-muted" aria-label="Clear search">
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                ) : null}
            </div>

            {/* Category tabs */}
            {!query ? (
                <div className="flex overflow-x-auto border-b" role="tablist" aria-label="Emoji categories">
                    {([["recent", CATEGORY_ICONS["recent"] ?? "🕐", "Recently used"]] as [string, string, string][])
                        .concat(CATEGORY_ORDER.map((c) => [c, categoryIcon(c), CATEGORY_LABELS[c] ?? c]))
                        .map(([id, icon, label]) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={activeCategory === id}
                                onClick={() => setActiveCategory(id as EmojiCategory | "recent")}
                                title={label}
                                className={cn(
                                    "shrink-0 px-2 py-1.5 text-base hover:bg-muted",
                                    activeCategory === id && "border-b-2 border-primary bg-primary/5"
                                )}
                            >
                                {icon}
                            </button>
                        ))}
                </div>
            ) : null}

            {/* Emoji grid */}
            <div
                ref={gridRef}
                className="max-h-52 overflow-y-auto p-1"
                onKeyDown={onGridKeyDown}
                aria-label={query ? `Emoji search results for "${query}"` : CATEGORY_LABELS[activeCategory as EmojiCategory] ?? "Recently used"}
            >
                {visibleEmojis.length === 0 ? (
                    <p className="py-6 text-center text-xs text-muted-foreground">
                        {query ? "No emoji found" : "No recent emoji yet"}
                    </p>
                ) : (
                    <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))` }}>
                        {visibleEmojis.map((emoji, idx) => (
                            <button
                                key={`${emoji}-${idx}`}
                                type="button"
                                className="emoji-cell flex h-8 w-8 items-center justify-center rounded text-lg hover:bg-muted focus:bg-accent focus:outline-none"
                                aria-label={emoji}
                                tabIndex={idx === focusIdx ? 0 : -1}
                                onMouseEnter={() => setFocusIdx(idx)}
                                onClick={() => pickEmoji(emoji)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer: skin tone picker */}
            <div className="flex items-center justify-between border-t px-2 py-1.5">
                <span className="text-[10px] text-muted-foreground">
                    {query ? `${visibleEmojis.length} result${visibleEmojis.length !== 1 ? "s" : ""}` : CATEGORY_LABELS[activeCategory as EmojiCategory] ?? "Recent"}
                </span>
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowTonePicker((v) => !v)}
                        title={`Skin tone: ${SKIN_TONE_LABELS[skinTone]}`}
                        className="rounded px-1.5 py-0.5 text-base hover:bg-muted"
                        aria-label="Choose skin tone"
                    >
                        {SKIN_TONE_SWATCHES[skinTone]}
                    </button>
                    {showTonePicker ? (
                        <div className="absolute bottom-full right-0 mb-1 flex gap-0.5 rounded-md border bg-popover p-1 shadow-md">
                            {SKIN_TONE_SWATCHES.map((swatch, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => changeSkinTone(i)}
                                    title={SKIN_TONE_LABELS[i]}
                                    aria-pressed={skinTone === i}
                                    className={cn(
                                        "rounded px-1 py-0.5 text-base hover:bg-muted",
                                        skinTone === i && "ring-2 ring-primary"
                                    )}
                                >
                                    {swatch}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function categoryIcon(category: EmojiCategory): string {
    return CATEGORY_ICONS[category] ?? "🙂";
}

// ────────────────────────────────────────────────────────────────────────────
//  Trigger button + popover wrapper (for use in Composer)
// ────────────────────────────────────────────────────────────────────────────

interface EmojiPickerTriggerProps {
    onSelect: (emoji: string) => void;
    /** className for the trigger button */
    className?: string;
}

export function EmojiPickerTrigger({ onSelect, className }: EmojiPickerTriggerProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const popRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

    // Close on outside click (the picker lives in a portal, so check both).
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (ref.current?.contains(t) || popRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    // Place the picker above the button (below it when there is no room), clamped inside the viewport, so it can
    // never stick out past the window and widen the page.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        const place = () => {
            const btn = ref.current?.getBoundingClientRect();
            if (!btn) return;
            const pop = popRef.current?.getBoundingClientRect();
            const w = pop?.width ?? 320;
            const h = pop?.height ?? 330;
            const margin = 8;
            const left = Math.max(margin, Math.min(btn.left, window.innerWidth - w - margin));
            let top = btn.top - h - margin;
            if (top < margin) top = Math.min(btn.bottom + margin, Math.max(margin, window.innerHeight - h - margin));
            setPos({ left, top });
        };
        place();
        window.addEventListener("resize", place);
        return () => window.removeEventListener("resize", place);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Emoji picker"
                aria-expanded={open}
                title="Emoji"
                className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted",
                    className
                )}
            >
                <span aria-hidden className="text-base">😊</span>
            </button>
            {open
                ? createPortal(
                    <div
                        ref={popRef}
                        className="z-[70]"
                        style={{ position: "fixed", left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? "visible" : "hidden", maxWidth: "calc(100vw - 16px)" }}
                    >
                        <EmojiPicker onSelect={onSelect} onClose={() => setOpen(false)} />
                    </div>,
                    document.body
                )
                : null}
        </div>
    );
}
